'use client';

import { ProductCard } from '@components/Product/ProductCard';
import { ProductCardProps } from '@components/Product/ProductCard.types';
import { useEntitlements } from '@contexts/EntitlementsContext';
import { useProductLists } from '@contexts/ProductListsContext';
import { useRouter } from '@contexts/Router/RouterContext';
import type { Result } from '@coveo/headless-react/ssr';
import {
  getActiveSale,
  getDiscountPercent,
  getSalePrice,
} from '@lib/coveo/sale';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { Sale, SaleType } from '@lib/types/Sale';
import { getParentCategory } from '@lib/util/category';
import * as Sentry from '@sentry/nextjs';
import { DEFAULT_CURRENCY } from 'utils';
import { useSurfaceHub } from './SearchSurfaceContext';
import { useSearchControllers } from './headless/SearchDefinitionContext';
import { useResultClickAnalytics } from './headless/useResultClickAnalytics';
import { parseProductId } from './parseProductId';

const VERIFIED_SOLUTION_TAG_SLUG = 'vsp';

export type ResultCardProps = {
  result: Result;
  className?: string;
};

export const ResultCard = ({ result, className }: ResultCardProps) => {
  const { locale } = useRouter();
  const hub = useSurfaceHub();
  // The display currency must match the currency of the engine's result values,
  // so read it off the dictionary field context the server seeded the query
  // with (SearchSsrPage); empty context means the index default, USD. Reading
  // the shopper's cookie here instead (useCurrency) would hydration-mismatch:
  // the cookie-backed provider renders the default currency on the server and
  // the cookie value on the client's first render.
  const controllers = useSearchControllers();
  const { state: dictionaryFieldContext } =
    controllers.useDictionaryFieldContext();
  const currency = dictionaryFieldContext.values.ec_price ?? DEFAULT_CURRENCY;
  const { getUserEntitlement } = useEntitlements();
  const { isProductInList, toggleProductList } = useProductLists();
  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const resultClickHandlers = useResultClickAnalytics(result);

  const {
    ec_name,
    ec_product_id,
    ec_thumbnails,
    publisher_id,
    publisher_name,
    ec_rating,
    ec_rating_count,
    ec_price,
    ec_sale,
    ec_flash_teaser_starts_at,
    ec_category,
    ec_is_new_release,
    ec_tags,
  } = result.raw;

  const parentCategory = getParentCategory(ec_category as string[] | undefined);

  let sales: Sale[] = [];
  try {
    sales = ec_sale ? (JSON.parse(ec_sale as string) as Sale[]) : [];
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTags({
        'coveo.hub': hub,
        'coveo.step': 'ec_sale_parse',
      });
      Sentry.captureException(e);
    });
  }
  const sale = getActiveSale(sales, ec_flash_teaser_starts_at as string);

  const productId = parseProductId(ec_product_id);
  const entitlement = getUserEntitlement(productId);

  const isVerifiedSolution =
    (ec_tags as string[])?.includes(VERIFIED_SOLUTION_TAG_SLUG) ?? false;

  const commonProps: ProductCardProps = {
    name: ec_name as string,
    productId,
    imageUrl: ec_thumbnails as string,
    publisher: {
      name: publisher_name as string,
      id: publisher_id as string,
    },
    rating: ec_rating as number,
    ratingCount: ec_rating_count as number,
    price: {
      currentPrice: sale ? getSalePrice(sale, currency) : (ec_price as number),
      originalPrice: ec_price as number,
      currency,
      discountPercent: sale?.discount.value
        ? getDiscountPercent(sale)
        : undefined,
    },
    isVerifiedSolution,
    showFavorite: isProductListsEnabled,
    isFavorite: isProductListsEnabled ? isProductInList(productId) : undefined,
    sale: sale
      ? {
          type: sale.type ?? SaleType.Sale,
          dates: {
            starts: new Date(sale.dates.starts),
            ends: new Date(sale.dates.ends),
          },
        }
      : undefined,
    entitlement,
    locale,
    extraEventHandlers: {
      ...resultClickHandlers,
      onFavorite: isProductListsEnabled
        ? () => toggleProductList({ id: productId, name: ec_name as string })
        : undefined,
    },
    className,
  };

  return (
    <ProductCard
      {...commonProps}
      category={parentCategory}
      isNewRelease={ec_is_new_release as boolean}
    />
  );
};
